import { useTranslation } from 'react-i18next';
import {
  MessageBar, MessageBarBody, Spinner, Table, TableBody, TableCell, TableHeader, TableHeaderCell,
  TableRow, makeStyles, tokens,
} from '@fluentui/react-components';
import { DataTrendingRegular } from '@fluentui/react-icons';
import { useIsStateTaxAdmin } from '../../services/stateTaxClientsApi';
import { useUsage } from '../../services/complianceApi';
import { apiErrorMessage } from '../../lib/apiErrorMessage';

// STX-PROD-01 — the app's own usage number, on the Management page, admins only. Totals per week,
// never per person: "is the app used, and how", not a leaderboard. The server (UsageMetrics) counts
// from the app's own records; operator backfills are excluded there.

const useStyles = makeStyles({
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  summary: { color: tokens.colorNeutralForeground2 },
  tableCard: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflowX: 'auto',
  },
  num: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
});

export default function UsageCard() {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const { data: isAdmin = false } = useIsStateTaxAdmin();
  const { data, isLoading, isError, error } = useUsage(isAdmin);

  if (!isAdmin) return null;

  const weeks = data?.weeks ?? [];
  const newestFirst = [...weeks].reverse();
  const total = (k: 'filedInApp' | 'filedViaCrm' | 'sourcesUploaded') => weeks.reduce((n, w) => n + w[k], 0);
  const activeWeeks = weeks.filter((w) => w.filedInApp + w.filedViaCrm + w.sourcesUploaded > 0).length;
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const fmt = (iso: string) =>
    new Date(`${iso}T00:00:00Z`).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

  return (
    <section className={styles.section} data-testid="usage-card">
      <div className={styles.sectionTitle}>
        <DataTrendingRegular />
        {t('managementPage.usage.title')}
      </div>
      {isError && (
        <MessageBar intent="error">
          <MessageBarBody>{apiErrorMessage(error, t, 'managementPage.usage.loadError')}</MessageBarBody>
        </MessageBar>
      )}
      {isLoading && <Spinner label={t('common.loading')} />}
      {!isLoading && !isError && weeks.length > 0 && (
        <>
          <span className={styles.summary} data-testid="usage-summary">
            {t('managementPage.usage.summary', {
              weeks: weeks.length,
              filedInApp: total('filedInApp'),
              filedViaCrm: total('filedViaCrm'),
              sources: total('sourcesUploaded'),
              active: activeWeeks,
            })}
          </span>
          <div className={styles.tableCard}>
            <Table size="small" aria-label={t('managementPage.usage.title')}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>{t('managementPage.usage.columns.week')}</TableHeaderCell>
                  <TableHeaderCell>{t('managementPage.usage.columns.filedInApp')}</TableHeaderCell>
                  <TableHeaderCell>{t('managementPage.usage.columns.filedViaCrm')}</TableHeaderCell>
                  <TableHeaderCell>{t('managementPage.usage.columns.sources')}</TableHeaderCell>
                  <TableHeaderCell>{t('managementPage.usage.columns.people')}</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {newestFirst.map((w) => (
                  <TableRow key={w.weekStart} data-testid="usage-row">
                    <TableCell>{fmt(w.weekStart)}</TableCell>
                    <TableCell className={styles.num}>{w.filedInApp}</TableCell>
                    <TableCell className={styles.num}>{w.filedViaCrm}</TableCell>
                    <TableCell className={styles.num}>{w.sourcesUploaded}</TableCell>
                    <TableCell className={styles.num}>{w.peopleFiling}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </section>
  );
}
